import React, { useState, useEffect, useCallback } from 'react';
import './Forms.css';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import formBanner from '../assets/forms.png';


// ─── Config ───────────────────────────────────────────────────────────────────
const MONDAY_BOARD_ID = process.env.REACT_APP_MONDAY_FORMS_BOARD_ID;
const MONDAY_API_URL  = 'https://api.monday.com/v2';
const MONDAY_TOKEN    = process.env.REACT_APP_MONDAY_TOKEN;


const mondayFetch = (query, variables = {}) =>
  fetch(MONDAY_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: MONDAY_TOKEN,
      'API-Version': '2024-01',
    },
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());


// ─── Hardcoded forms ──────────────────────────────────────────────────────────
// type: 'monday-form' → cannot embed, opens prompt modal to launch in new tab
const HARDCODED_FORMS = [
  {
    id: 'hc-travel',
    tag: 'Travel',
    title: 'Travel Request',
    description: 'Submit a travel request for approval before booking.',
    type: 'monday-form',
    src: 'https://wkf.ms/4lhV4or',
  },
  {
    id: 'hc-it',
    tag: 'IT Support',
    title: 'IT Support Ticket',
    description: 'Submit a ticket for IT assistance or technical issues.',
    type: 'monday-form',
    src: 'https://wkf.ms/4cRAM1m',
  },
  {
    id: 'hc-admin',
    tag: 'Admin',
    title: 'Administration Request',
    description: 'Submit a general administration request or inquiry.',
    type: 'monday-form',
    src: 'https://wkf.ms/4cRAM1m',
  },
];


// ─── Monday API helpers ───────────────────────────────────────────────────────
async function fetchBoardItems() {
  const query = `
    query {
      boards(ids: ${MONDAY_BOARD_ID}) {
        items_page(limit: 50) {
          items {
            id
            name
            column_values(ids: ["file_mm3fd690"]) {
              id
              value
            }
          }
        }
      }
    }
  `;
  const json = await mondayFetch(query);
  if (json.errors) throw new Error(json.errors[0].message);
  return json?.data?.boards?.[0]?.items_page?.items ?? [];
}


async function fetchAssetUrls(assetIds) {
  if (!assetIds.length) return {};
  const query = `
    query ($ids: [ID!]!) {
      assets(ids: $ids) {
        id
        name
        public_url
        file_extension
      }
    }
  `;
  const json = await mondayFetch(query, { ids: assetIds.map(Number) });
  if (json.errors) throw new Error(json.errors[0].message);
  const map = {};
  for (const asset of json?.data?.assets ?? []) {
    map[String(asset.id)] = asset;
  }
  return map;
}


async function fetchMondayForms() {
  const items = await fetchBoardItems();
  const assetIds      = [];
  const itemAssetMeta = {};

  for (const item of items) {
    const raw = item.column_values?.[0]?.value;
    if (!raw) continue;
    try {
      const parsed    = JSON.parse(raw);
      const firstFile = parsed?.files?.[0];
      if (firstFile?.assetId) {
        const aid = String(firstFile.assetId);
        assetIds.push(Number(aid));
        itemAssetMeta[item.id] = {
          assetId:  aid,
          fileName: firstFile.name || item.name,
        };
      }
    } catch (_) {}
  }

  const assetMap = await fetchAssetUrls(assetIds);

  return items
    .map((item) => {
      const meta  = itemAssetMeta[item.id];
      const asset = meta ? assetMap[meta.assetId] : null;
      if (!asset?.public_url) return null;

      const ext   = (asset.file_extension || '').toLowerCase().replace('.', '');
      const isPDF = ext === 'pdf' || asset.name?.toLowerCase().endsWith('.pdf');

      return {
        id:          item.id,
        tag:         deriveTag(item.name),
        title:       item.name,
        description: `Download the ${item.name} form.`,
        // pdf and file both behave the same — download only, no modal
        type:        isPDF ? 'pdf' : 'file',
        src:         asset.public_url,
        fileName:    asset.name || meta.fileName,
      };
    })
    .filter(Boolean);
}


function deriveTag(name = '') {
  const n = name.toLowerCase();
  if (n.includes('travel'))                                                                        return 'Travel';
  if (n.includes('petty') || n.includes('cash') || n.includes('finance') || n.includes('procure')) return 'Finance';
  if (n.includes('health') || n.includes('safety') || n.includes('h&s'))                         return 'H&S';
  if (n.includes('meeting') || n.includes('minutes') || n.includes('admin'))                     return 'Admin';
  if (n.includes('legal') || n.includes('compliance'))                                           return 'Legal';
  if (n.includes('it') || n.includes('support') || n.includes('ticket'))                        return 'IT Support';
  return 'General';
}


// ─── Download helper ──────────────────────────────────────────────────────────
function triggerDownload(url, fileName) {
  const a   = document.createElement('a');
  a.href    = url;
  a.download = fileName || 'download';
  a.target  = '_blank';
  a.rel     = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}


// ─── Icons ────────────────────────────────────────────────────────────────────
function DownloadIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ExternalLinkIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function FormLinkIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="14" y2="12" />
      <line x1="8" y1="16" x2="12" y2="16" />
    </svg>
  );
}


// ─── Modal (Monday/MS forms only) ─────────────────────────────────────────────
function Modal({ item, onClose }) {
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!item) return null;

  return (
    <div
      className="forms-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <div
        className="forms-modal forms-modal--prompt"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="forms-modal-header">
          <div className="forms-modal-header-left">
            <span className="forms-card-tag">{item.tag}</span>
            <h2 className="forms-modal-title">{item.title}</h2>
          </div>
          <div className="forms-modal-header-actions">
            <button className="forms-modal-close" onClick={onClose} aria-label="Close modal">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body — external form prompt only */}
        <div className="forms-modal-body">
          <div className="forms-modal-external-prompt">
            <div className="forms-modal-file-icon"><FormLinkIcon /></div>
            <p className="forms-modal-file-name">{item.title}</p>
            <p className="forms-modal-file-hint">
              This form cannot be embedded here. Click below to open it in a new tab.
            </p>
            <a
              href={item.src}
              target="_blank"
              rel="noopener noreferrer"
              className="forms-modal-open-btn"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLinkIcon size={14} />
              <span>Open Form</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Form Card ────────────────────────────────────────────────────────────────
function FormCard({ form, onOpen }) {
  const { tag, title, description, type, src } = form;
  const isMondayForm = type === 'monday-form';
  const isDownloadable = (type === 'pdf' || type === 'file') && !!src;
  const hasAction = !!src;

  // Monday/MS forms → card click opens modal prompt
  // PDF/file cards  → card click does NOTHING (download button only)
  const handleCardClick = () => {
    if (isMondayForm) onOpen(form);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    triggerDownload(src, form.fileName || title);
  };

  return (
    <div
      className={`forms-card ${isMondayForm ? 'forms-card--clickable' : ''}`}
      onClick={handleCardClick}
      role={isMondayForm ? 'button' : undefined}
      tabIndex={isMondayForm ? 0 : undefined}
      onKeyDown={isMondayForm ? (e) => (e.key === 'Enter' || e.key === ' ') && handleCardClick() : undefined}
    >
      <div className="forms-card-header">
        <span className="forms-card-tag">{tag}</span>
        {title && <h2>{title}</h2>}

        {hasAction && (
          <div className="forms-card-actions" onClick={(e) => e.stopPropagation()}>
            {/* Download button — PDF/file only */}
            {isDownloadable && (
              <button
                className="forms-icon-btn"
                title={`Download ${title}`}
                aria-label={`Download ${title}`}
                onClick={handleDownload}
              >
                <DownloadIcon />
              </button>
            )}
            {/* External link indicator for monday/ms forms */}
            {isMondayForm && (
              <span className="forms-icon-btn forms-icon-btn--external" aria-hidden="true">
                <ExternalLinkIcon size={12} />
              </span>
            )}
          </div>
        )}
      </div>

      <div className="forms-card-body">
        <p className={`forms-placeholder ${!hasAction ? 'forms-placeholder--empty' : ''}`}>
          {hasAction ? description : 'No forms added yet.'}
        </p>
      </div>
    </div>
  );
}


// ─── Skeleton Card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="forms-card forms-card--skeleton" aria-hidden="true">
      <div className="forms-card-header">
        <span className="forms-skeleton forms-skeleton--tag" />
        <span className="forms-skeleton forms-skeleton--title" />
      </div>
      <div className="forms-card-body">
        <span className="forms-skeleton forms-skeleton--line" />
        <span className="forms-skeleton forms-skeleton--line forms-skeleton--line-short" />
      </div>
    </div>
  );
}


// ─── Layout config ────────────────────────────────────────────────────────────
const LEFT_TAGS  = ['Travel', 'IT Support', 'H&S'];
const RIGHT_TAGS = ['Finance', 'Admin', 'Legal', 'General'];

const STATIC_PLACEHOLDERS = [
  { id: 'ph-hs',      tag: 'H&S',    title: 'Health & Safety',       type: 'empty', src: null },
  { id: 'ph-finance', tag: 'Finance', title: 'Finance & Procurement', type: 'empty', src: null },
];


// ─── Page ─────────────────────────────────────────────────────────────────────
const Forms = () => {
  const [modalItem,  setModalItem]  = useState(null);
  const [cmsForms,   setCmsForms]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const loadForms = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const forms = await fetchMondayForms();
      setCmsForms(forms);
    } catch (err) {
      console.error('Monday CMS fetch failed:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadForms(); }, [loadForms]);

  const allForms     = [...HARDCODED_FORMS, ...cmsForms];
  const coveredTags  = new Set(allForms.map((f) => f.tag));
  const placeholders = STATIC_PLACEHOLDERS.filter((p) => !coveredTags.has(p.tag));

  const leftForms  = [
    ...allForms.filter((f) => LEFT_TAGS.includes(f.tag)),
    ...placeholders.filter((p) => LEFT_TAGS.includes(p.tag)),
  ];
  const rightForms = [
    ...allForms.filter((f) => RIGHT_TAGS.includes(f.tag)),
    ...placeholders.filter((p) => RIGHT_TAGS.includes(p.tag)),
  ];

  return (
    <div className="forms-page">
      <NavBar />

<div
  className="forms-banner"
  style={{ backgroundImage: `url(${formBanner})` }}
>
  <div className="forms-banner-overlay" />
  <div className="forms-banner-text">
    <h2 className="forms-banner-heading">Forms</h2>
    <p className="forms-banner-sub">Download and submit official forms</p>
  </div>
</div>

      <div className="forms-content">
        {fetchError && (
          <div className="forms-error-banner" role="alert">
            <span>Could not load forms from Monday.com.</span>
            <button onClick={loadForms} className="forms-retry-btn">Retry</button>
          </div>
        )}

        <div className="forms-grid">
          <div className="forms-column">
            {leftForms.map((form) => (
              <FormCard key={form.id} form={form} onOpen={setModalItem} />
            ))}
            {loading && [0, 1].map((i) => <SkeletonCard key={`sk-l-${i}`} />)}
          </div>
          <div className="forms-column">
            {rightForms.map((form) => (
              <FormCard key={form.id} form={form} onOpen={setModalItem} />
            ))}
            {loading && [0, 1].map((i) => <SkeletonCard key={`sk-r-${i}`} />)}
          </div>
        </div>
      </div>

      <Footer />

      {/* Modal only renders for monday-form type cards */}
      {modalItem && (
        <Modal item={modalItem} onClose={() => setModalItem(null)} />
      )}
    </div>
  );
};

export default Forms;